import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { OpenService } from '@patsnap/tahiti-core/services/open-service';
import { isUserLogin, addTrialDeferred } from '@eureka/utils/account';
import type { AuthService } from '../auth-service';
import type { AuthModuleData, AuthPageParams, ShareAuthCheckData } from '../../types';
import { AuthModel } from '../../models/auth.model';
import { ShareAuthApi } from '../../apis/share-auth-api';
import { ejectAirShareInterceptor, injectAirShareInterceptor } from '@eureka/services/src/apis/interceptors/share.interceptor';
import { Site, SLD } from '@patsnap-utils/biz';

@injectable()
export class AuthServiceImpl implements AuthService {
    private _pageParams: AuthPageParams = {
        currentPageType: '',
        currentDetailId: '',
    }

    constructor(
        @inject(ShareAuthApi) private _shareAuthApi: ShareAuthApi,
        @inject(AuthModel) private _authModel: AuthModel,
        @inject(OpenService) private _openService: OpenService
    ) {}

    private async _getOwnerInfo(data: ShareAuthCheckData) {
        try {
            const res = await this._shareAuthApi.getShareOwnerInfo(data);
            const response = {
                ownerName: res.owner_name,
                ownerId: res.owner_id,
                canCopy: res.can_copy,
                canCreateShare: res.can_create_share,
            };
            this._updateAuthState({
                ownerName: response.ownerName,
                ownerId: response.ownerId,
                canCopy: response.canCopy,
                canCreateShare: response.canCreateShare,
            })
            return response;
        } catch (err) {
            this._handleError('Failed to get owner info', err);
            return { ownerName: '', ownerId: '', canCopy: false, canCreateShare: false }
        }
    }

    private _updateAuthState(state: Partial<AuthModuleData>) {
        this._authModel.updateModuleData(v => ({
            ...v,
            ...state,
        }))
    }

    private _backToHome() {
        const url = new Site({ sld: SLD.EUREKA, pathname: 'rd' }).href;
        this._openService.open(url, {
            target: '_self',
        });
    }

    private _handleError(message: string, error: any) {
        console.error(message, error);
        // Additional logging or error handling can be added here
    }

    async shareAccessCheck(data?: ShareAuthCheckData) {
        try {
            const isLogin = await isUserLogin();
            this._updateAuthState({ isLogin });
            if (isLogin) {
                await addTrialDeferred.promise;
            }

            const isShare = await this._determineShareAccess(isLogin, data);

            if (isShare || !isLogin) {
                injectAirShareInterceptor();
                this._updateAuthState({ isShare });
                if (data) {
                    this._getOwnerInfo(data);
                }
            } else {
                this._denyAccess();
            }

            return isShare;
        } catch (err) {
            this._handleError('Share access check failed', err);
            this._denyAccess();
            this._backToHome();
            throw err
        }
    }

    private async _determineShareAccess(isLogin: boolean, data?: ShareAuthCheckData): Promise<boolean> {
        if (data?.data_id && data?.data_type) {
            return isLogin
                ? await this._shareAuthApi.shareAccessCheck(data)
                : await this._shareAuthApi.shareAccessCheckForGuest(data);
        }
        return false;
    }

    private _denyAccess() {
        ejectAirShareInterceptor();
        this._updateAuthState({
            isShare: false,
            ownerName: '',
            canCopy: false,
            canCreateShare: false,
        });
    }

    get pageParams() {
        return this._pageParams;
    }

    setPageParams(params: Partial<typeof this._pageParams>) {
        this._pageParams = {
            ...this._pageParams,
            ...params,
        }
    }
}
